import { customRef, onScopeDispose, ref, watch, type Ref, type WatchSource } from 'vue'

/**
 * A value that follows its source once the source has been still for `ms`
 * (a search box queries the API when the typing stops, not at every key).
 */
export function useDebounced<T>(source: WatchSource<T>, ms = 300): Ref<T> {
  const read = typeof source === 'function' ? source as () => T : () => (source as Ref<T>).value
  const out = ref(read()) as Ref<T>
  let timer: ReturnType<typeof setTimeout> | undefined
  watch(source, (v) => {
    clearTimeout(timer)
    timer = setTimeout(() => { out.value = v }, ms)
  })
  onScopeDispose(() => clearTimeout(timer))
  return out
}

/** A ref whose writes land after `ms` of stillness (a text field bound with v-model). */
export function debouncedRef<T>(initial: T, ms = 300): Ref<T> {
  let value = initial
  let timer: ReturnType<typeof setTimeout> | undefined
  return customRef((track, trigger) => ({
    get() { track(); return value },
    set(v: T) {
      clearTimeout(timer)
      timer = setTimeout(() => { value = v; trigger() }, ms)
    },
  }))
}
